import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import Spinner from "../../components/Shared/Spinner";
import Input from "../../components/Shared/Input";
import Selectbox, { option } from "../../components/Shared/SelectBox";
import { GetBooksRequests } from "../../api/Books/GotBooksRequests";
import Button from "../../components/Shared/Button";
import { BookCopy, ShoppingCart } from "lucide-react";
import { EditTransaction } from "../../api/Books/EditTransactionStatus";
import { queryClient } from "../../main";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import { useUserDataStore } from "../../stores/user";
const statusOptions: option[] = [
  { name: "pending" },
  { name: "in_shipping" },
  { name: "completed" },
];
const paymentOptions: option[] = [{ name: "success" }, { name: "failed" }];

const BookRequests = () => {
  const { userData } = useUserDataStore();

  const [page, setPage] = useState(1);
  const [limit] = useState(6);
  const [jumpPage, setJumpPage] = useState(1);
  const [firstName, setFirstName] = useState("");
  const [paymentStatus, setPaymentStatus] = useState<option | null>(null);
  const [shippingStatus, setShippingStatus] = useState<option | null>(null);
  const [phone, setPhone] = useState("");

  const [debouncedFirstName, setDebouncedFirstName] = useState(firstName);
  const [debouncedPhone, setDebouncedPhone] = useState(phone);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedFirstName(firstName);
      setDebouncedPhone(phone);
    }, 1000);

    return () => {
      clearTimeout(handler);
    };
  }, [firstName, phone]);

  const { data: StudentsResponse, isLoading: isStudentsLoading } = useQuery({
    queryFn: () =>
      GetBooksRequests({
        page,
        limit,
        firstName: debouncedFirstName,
        shipping_status: shippingStatus?.name,
        payment_status: paymentStatus?.name,
        phone: debouncedPhone,
      }),
    queryKey: [
      "books",
      page,
      limit,
      debouncedFirstName,
      shippingStatus,
      paymentStatus,
      debouncedPhone,
    ],
    staleTime: 0,
    refetchInterval: 0,
  });
  const { mutate: mutatePinned } = useMutation({
    mutationFn: ({
      shipping_status,
      id,
    }: {
      shipping_status: string;
      id: string;
    }) => EditTransaction({ shipping_status }, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["books"] });
      toast.success("Status Updated Successfully");
    },
    onError: (error) => {
      toast.error("Failed to update the Status");
      console.error(error);
    },
  });
  const handleUpdateStatus = (currentStatus: string, id: string) => {
    if (currentStatus === "completed") return;

    const newStatus =
      currentStatus === "pending"
        ? "in_shipping"
        : currentStatus === "in_shipping"
        ? "completed"
        : currentStatus;

    mutatePinned({ shipping_status: newStatus, id });
  };

  const handleNextPage = () => setPage((prev) => prev + 1);
  const handlePreviousPage = () => setPage((prev) => Math.max(prev - 1, 1));
  const handleJumpPage = () => setPage(jumpPage);
  const handleClearStatus = () => setShippingStatus(null);
  const handleClearPaymentStatus = () => setPaymentStatus(null);

  const data = StudentsResponse?.data?.bookTransactions || [];
  const metaData = StudentsResponse?.data?.metaData;

  if (isStudentsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="py-7">
      <h1 className="text-burntBlack font-bold text-[32px] mb-7">Books</h1>
      <div className="p-8 rounded-xl bg-white border-black/15 shadow">
        <div className="flex items-center justify-between mb-8">
          <h1 className="font-bold text-2xl">Books Requests</h1>
          {userData?.role === "sales" && (
            <Link to={"/sales/dashboard/books"}>
              <Button
                className="!h-12 w-44 !text-sm font-bold rounded-md !bg-[#4379EE] hover:!bg-primary"
                variant="primary"
              >
                <BookCopy />
                Books
              </Button>
            </Link>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Input
            name="FirstName"
            type="text"
            className="md:!space-y-0"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="First Name"
            inputStyles="!rounded !h-[50px]"
          />
          <div className="relative">
            <Selectbox
              selectOptions={statusOptions}
              selected={shippingStatus}
              setSelected={setShippingStatus}
              placeholder="Select Shipping Status"
              className="!rounded"
              titleStyles="!mb-0"
            />
            {shippingStatus && (
              <button
                onClick={handleClearStatus}
                className="absolute top-1/2 right-4 transform -translate-y-1/2 text-red-500 hover:text-red-700"
              >
                Clear
              </button>
            )}
          </div>
          <div className="relative">
            <Selectbox
              selectOptions={paymentOptions}
              selected={paymentStatus}
              setSelected={setPaymentStatus}
              placeholder="Select Payment Status"
              className="!rounded"
              titleStyles="!mb-0"
            />
            {paymentStatus && (
              <button
                onClick={handleClearPaymentStatus}
                className="absolute top-1/2 right-4 transform -translate-y-1/2 text-red-500 hover:text-red-700"
              >
                Clear
              </button>
            )}
          </div>

          <Input
            name="Phone"
            type="text"
            className="md:!space-y-0"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="Phone"
            inputStyles="!rounded !h-[50px]"
          />
        </div>

        <div className="space-y-5">
          {data?.map((request) => (
            <div className="border rounded-2xl px-4 py-6 space-y-4">
              <div>
                <h1 className="mb-2 text-primary font-bold text-xl">
                  Request Info:
                </h1>
                <div className="grid grid-cols-3 items-center gap-4">
                  <h1 className="font-semibold text-xl ">
                    Payment Status:{" "}
                    <span
                      className={`capitalize ${
                        request.payment_status === "success"
                          ? "text-green-500"
                          : "text-red-500"
                      }`}
                    >
                      {request.payment_status}
                    </span>
                  </h1>
                  <h2 className="font-semibold text-xl ">
                    Status:{" "}
                    <span
                      className={`capitalize ${
                        request.shipping_status === "pending"
                          ? "text-red-500"
                          : request.shipping_status === "in_shipping"
                          ? "text-primary"
                          : request.shipping_status === "completed"
                          ? "text-green-500"
                          : ""
                      }`}
                    >
                      {request.shipping_status === "in_shipping"
                        ? "In Shipping"
                        : request.shipping_status}
                    </span>
                  </h2>
                  <Button
                    onClick={() =>
                      handleUpdateStatus(request.shipping_status, request.id)
                    }
                    disabled={request.shipping_status === "completed"}
                    variant="primary"
                    className={`!h-14 ${
                      request.shipping_status === "completed"
                        ? "opacity-70 cursor-not-allowed"
                        : ""
                    }`}
                  >
                    Update Status <ShoppingCart />
                  </Button>
                </div>
              </div>
              <div>
                <h1 className="mb-2 text-primary font-bold text-xl">
                  User Info:
                </h1>
                <div className="grid grid-cols-3 gap-4">
                  <h1 className="font-medium text-sm">
                    Full Name: {request.student.firstName}{" "}
                    {request.student.lastName}
                  </h1>

                  <p className="font-medium text-sm">
                    Email Address: {request.student.email}
                  </p>
                  <p className="font-medium text-sm">
                    Phone Number: {request.student.phone}
                  </p>
                </div>
              </div>
              <div>
                <h1 className="mb-2 text-primary font-bold text-xl">
                  Book Info:
                </h1>
                <div className="grid grid-cols-3 gap-4">
                  <h1 className="font-medium text-sm">
                    Book Name: {request.book.name}
                  </h1>

                  <p className="font-medium text-sm">
                    Book Author: {request.book.author?.firstName}
                  </p>
                  <p className="font-medium text-sm">
                    Book Price: {request.book.public_EGP_price}EGP
                  </p>
                </div>
              </div>
              <div>
                <h1 className="mb-2 text-primary font-bold text-xl">
                  Billing Info:
                </h1>
                <div className="grid grid-cols-3 gap-4">
                  <h1 className="font-medium text-sm">
                    Country: {request.adress?.country}
                  </h1>

                  <p className="font-medium text-sm">
                    City: {request.adress?.city}
                  </p>
                  <p className="font-medium text-sm">
                    State: {request.adress?.state}
                  </p>
                  <p className="font-medium text-sm">
                    Street Name: {request.adress?.street}
                  </p>
                  <h1 className="font-medium text-sm">
                    Building Number: {request.adress?.building}
                  </h1>

                  <p className="font-medium text-sm">
                    Floor Number: {request.adress?.floor}
                  </p>
                  <p className="font-medium text-sm">
                    Apartment Number: {request.adress?.apartment}
                  </p>
                  <p className="font-medium text-sm">
                    Special Landing: {request.adress?.landing}
                  </p>
                  <p className="font-medium text-sm col-span-3">
                    Full Address: {request.adress?.country}, &nbsp;
                    {request.adress?.city}, &nbsp;{request.adress?.state},
                    &nbsp;{request.adress?.street}, &nbsp;
                    {request.adress?.building}, &nbsp;{request.adress?.floor},
                    &nbsp;{request.adress?.apartment}, &nbsp;
                    {request.adress?.landing},
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="flex justify-between items-center mt-4">
          <button
            onClick={handlePreviousPage}
            disabled={page === 1}
            className="bg-blue-500 text-white px-4 py-2 rounded disabled:bg-gray-400"
          >
            Previous
          </button>
          <span>
            Page {page} of {metaData?.totalPages || 1}
          </span>
          <button
            onClick={handleNextPage}
            disabled={!metaData || page >= metaData.totalPages}
            className="bg-blue-500 text-white px-4 py-2 rounded disabled:bg-gray-400"
          >
            Next
          </button>
        </div>

        <div className="flex justify-center items-center mt-4">
          <Input
            name="Page"
            type="number"
            value={jumpPage.toString()}
            className="md:!space-y-0"
            inputStyles="!h-10 !rounded"
            onChange={(e) => setJumpPage(Number(e.target.value))}
          />
          <button
            onClick={handleJumpPage}
            className="bg-blue-500 text-white px-4 h-10 rounded disabled:bg-gray-400"
          >
            Jump to Page
          </button>
        </div>
      </div>
    </div>
  );
};

export default BookRequests;
